import type { MergeDropdownCheckRun } from './types';

type CheckRun = MergeDropdownCheckRun;

/**
 * Returns a string representation of the check's current status.
 *
 * @param {CheckRun} check - A check run object.
 * @returns {string} The status of the check or `'unknown'` if status is unavailable.
 */
export function getCheckStatus(check: CheckRun): string {
  if (check.status === 'completed') {
    if (check.conclusion === null) {
      return 'unknown';
    }
    return check.conclusion;
  }
  return check.status ?? 'unknown';
}

/**
 * Splits checks into two groups: "required" and "optional", based on the provided set of required names.
 *
 * @param {CheckRun[]} checks - An array of check runs.
 * @param {Set<string>} requiredNames - A set of required check names.
 * @returns {{ required: CheckRun[]; optional: CheckRun[] }} An object with two arrays: `required` and `optional`.
 */
export function groupChecksByRequirement(
  checks: CheckRun[],
  requiredNames: Set<string>,
): { required: CheckRun[]; optional: CheckRun[] } {
  return checks.reduce(
    (acc, check) => {
      const isRequired = requiredNames.has(check.name);
      acc[isRequired ? 'required' : 'optional'].push(check);
      return acc;
    },
    { required: [] as CheckRun[], optional: [] as CheckRun[] },
  );
}

/**
 * Groups checks by their status (e.g. success, failure, pending).
 *
 * @param {CheckRun[]} checks - An array of check runs.
 * @returns {Record<string, CheckRun[]>} An object keyed by status with arrays of checks as values.
 */
export function groupChecksByStatus(checks: CheckRun[]): Record<string, CheckRun[]> {
  return checks.reduce(
    (acc, check) => {
      const status = getCheckStatus(check);
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(check);
      return acc;
    },
    {} as Record<string, CheckRun[]>,
  );
}

/**
 * Determines if a check has completed successfully.
 *
 * @param {CheckRun} check - A check run object.
 * @returns {boolean} True if the check is complete and successful, otherwise false.
 */
export function isCheckPassing(check: CheckRun): boolean {
  return check.status === 'completed' && check.conclusion === 'success';
}

/**
 * Determines if a check has completed and failed.
 *
 * @param {CheckRun} check - A check run object.
 * @returns {boolean} True if the check is complete and failed, otherwise false.
 */
export function isCheckFailing(check: CheckRun): boolean {
  return check.status === 'completed' && check.conclusion === 'failure';
}
